import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import {
  LegalAcceptances,
  LegalDocumentOverview,
  LegalDocumentType,
  LegalDraft,
  LegalVersion,
  LegalVersionSummary,
  PendingLegalDocument,
  PublishLegalPayload,
} from "../../models/legal.model";

/** Documentos jurídicos (spec 029): leitura pública, aceite e o painel. */
@Injectable({ providedIn: "root" })
export class LegalService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/legal`;

  // ── Público ────────────────────────────────────────────────────────

  getCurrent(type: LegalDocumentType): Observable<LegalVersion> {
    return this.http.get<LegalVersion>(`${this.url}/documents/${type}`);
  }

  getVersion(type: LegalDocumentType, version: string): Observable<LegalVersion> {
    return this.http.get<LegalVersion>(
      `${this.url}/documents/${type}/versions/${version}`,
    );
  }

  listVersions(type: LegalDocumentType): Observable<LegalVersionSummary[]> {
    return this.http.get<LegalVersionSummary[]>(
      `${this.url}/documents/${type}/versions`,
    );
  }

  // ── Aceite de quem está logado ─────────────────────────────────────

  getPending(): Observable<PendingLegalDocument[]> {
    return this.http.get<PendingLegalDocument[]>(`${this.url}/me/pending`);
  }

  /** Só os tipos: a versão aceita é a vigente, decidida no servidor. */
  accept(types: LegalDocumentType[]): Observable<PendingLegalDocument[]> {
    return this.http.post<PendingLegalDocument[]>(`${this.url}/me/accept`, {
      types,
    });
  }

  // ── Painel da gerente ──────────────────────────────────────────────

  overview(): Observable<LegalDocumentOverview[]> {
    return this.http.get<LegalDocumentOverview[]>(`${this.url}/admin`);
  }

  getDraft(type: LegalDocumentType): Observable<LegalDraft> {
    return this.http.get<LegalDraft>(`${this.url}/admin/${type}/draft`);
  }

  saveDraft(
    type: LegalDocumentType,
    draft: { title: string; markdown: string },
  ): Observable<LegalDraft> {
    return this.http.put<LegalDraft>(`${this.url}/admin/${type}/draft`, draft);
  }

  discardDraft(type: LegalDocumentType): Observable<void> {
    return this.http.delete<void>(`${this.url}/admin/${type}/draft`);
  }

  publish(
    type: LegalDocumentType,
    payload: PublishLegalPayload,
  ): Observable<LegalVersion> {
    return this.http.post<LegalVersion>(
      `${this.url}/admin/${type}/publish`,
      payload,
    );
  }

  getAcceptances(type: LegalDocumentType): Observable<LegalAcceptances> {
    return this.http.get<LegalAcceptances>(
      `${this.url}/admin/${type}/acceptances`,
    );
  }
}
